'use client';

import { useEffect } from 'react';
import Lenis from 'lenis';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

// Register ScrollTrigger plugin
if (typeof window !== 'undefined') {
  gsap.registerPlugin(ScrollTrigger);
}

interface SmoothScrollProps {
  options?: {
    duration?: number;
    easing?: (t: number) => number;
    orientation?: 'vertical' | 'horizontal';
    gestureOrientation?: 'vertical' | 'horizontal' | 'both';
    smoothWheel?: boolean;
    smoothTouch?: boolean;
    touchMultiplier?: number;
    wheelMultiplier?: number;
    infinite?: boolean;
  };
}

/**
 * SmoothScroll component - Provides smooth scrolling with acceleration/deceleration and momentum
 * 
 * Features:
 * - Smooth acceleration and deceleration during scroll
 * - Momentum scrolling (continues scrolling after wheel stops with deceleration)
 * - Integrated with GSAP ScrollTrigger
 */
export default function SmoothScroll({ options }: SmoothScrollProps = {}) {
  useEffect(() => {
    // Initialize Lenis with smooth scrolling configuration
    const lenis = new Lenis({
      duration: options?.duration ?? 1.2, // Duration of scroll animation (affects acceleration/deceleration speed)
      easing: options?.easing ?? ((t) => Math.min(1, 1.001 - Math.pow(2, -10 * t))), // Easing function for smooth acceleration/deceleration
      orientation: options?.orientation ?? 'vertical',
      gestureOrientation: options?.gestureOrientation ?? 'vertical',
      smoothWheel: options?.smoothWheel ?? true, // Enable smooth wheel scrolling with momentum
      touchMultiplier: options?.touchMultiplier ?? 2,
      wheelMultiplier: options?.wheelMultiplier ?? 1, // Adjust scroll speed sensitivity
      infinite: options?.infinite ?? false,
    });

    // Animation loop for smooth scrolling
    // This creates the momentum effect - scroll continues with deceleration after wheel stops
    function raf(time: number) {
      lenis.raf(time);
      ScrollTrigger.update();
      requestAnimationFrame(raf);
    }

    // Start the animation loop
    requestAnimationFrame(raf);

    // Integrate with GSAP ScrollTrigger for scroll-triggered animations
    lenis.on('scroll', ScrollTrigger.update);

    // Cleanup
    return () => {
      lenis.destroy();
    };
  }, [options]);

  return null;
}

